/**
 * Centralized timestamp utilities for converting server timestamps to local machine time
 */

/**
 * Converts a server timestamp to local machine time
 * Handles various timestamp formats (ISO strings, Unix timestamps, etc.)
 * @param {string|number|Date} timestamp - The timestamp to convert
 * @returns {Date|null} - The converted Date object or null if invalid
 */
export const convertToLocalTime = (timestamp) => {
  if (!timestamp) return null;
  
  try {
    let date;
    
    // Handle different timestamp formats
    if (typeof timestamp === 'string' && /^\d+$/.test(timestamp)) {
      // Purely-numeric string (e.g. a Unix timestamp serialized as a string) —
      // `new Date("1784556365")` does NOT parse this as an epoch value like
      // `new Date(1784556365)` does, it fails as an invalid date string. Route
      // through the same seconds-vs-milliseconds numeric logic below instead.
      date = convertToLocalTime(Number(timestamp));
      return date;
    } else if (typeof timestamp === 'string') {
      // ISO string or other string format
      date = new Date(timestamp);
    } else if (typeof timestamp === 'number') {
      // Unix timestamp (check if it's in seconds or milliseconds)
      if (timestamp < 10000000000) {
        // Unix timestamp in seconds, convert to milliseconds
        date = new Date(timestamp * 1000);
      } else {
        // Unix timestamp in milliseconds
        date = new Date(timestamp);
      }
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else {
      return null;
    }
    
    // Validate the date
    if (isNaN(date.getTime())) {
      console.warn('Invalid timestamp provided:', timestamp);
      return null;
    }
    
    return date;
  } catch (error) {
    console.error('Error converting timestamp to local time:', error, timestamp);
    return null;
  }
};

/**
 * Formats a timestamp for display in message cards (inbox list)
 * Shows relative time: Today, Yesterday, or date
 * @param {string|number|Date} timestamp - The timestamp to format
 * @returns {string} - Formatted time string
 */
export const formatMessageTime = (timestamp) => {
  const date = convertToLocalTime(timestamp);
  if (!date) return timestamp || '-';
  
  try {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    if (messageDate.getTime() === today.getTime()) {
      // Today: show time only
      return date.toLocaleString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } else if (messageDate.getTime() === yesterday.getTime()) {
      // Yesterday: show "Yesterday" + time
      return `Yesterday ${date.toLocaleString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })}`;
    } else {
      // Other days: show date only
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    }
  } catch (error) {
    console.error('Error formatting message time:', error);
    return timestamp || '-';
  }
};

/**
 * Formats a timestamp for display in chat messages
 * Shows time with more detail for chat context
 * @param {string|number|Date} timestamp - The timestamp to format
 * @returns {string} - Formatted time string
 */
export const formatChatTime = (timestamp) => {
  // CRITICAL: Prevent formatting already-formatted time strings
  // If timestamp is already a formatted time string (contains "AM" or "PM" or "Yesterday"), return it as-is
  if (typeof timestamp === 'string') {
    // Check if it's already a formatted time string (not an ISO timestamp)
    if ((timestamp.includes('AM') || timestamp.includes('PM') || timestamp.includes('Yesterday') || timestamp.includes('Today')) 
        && !timestamp.includes('T') && !timestamp.match(/^\d{4}-\d{2}-\d{2}/)) {
      // It's already formatted, return as-is to prevent duplication
      return timestamp;
    }
  }
  
  const date = convertToLocalTime(timestamp);
  if (!date) return timestamp || '-';
  
  try {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    if (messageDate.getTime() === today.getTime()) {
      // Today: show time only
      return date.toLocaleString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } else if (messageDate.getTime() === yesterday.getTime()) {
      // Yesterday: show "Yesterday" + time
      return `Yesterday ${date.toLocaleString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })}`;
    } else {
      // Other days: show date + time
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }
  } catch (error) {
    console.error('Error formatting chat time:', error);
    return timestamp || '-';
  }
};

/**
 * Formats a timestamp for display in date separators
 * Shows full date information
 * @param {string|number|Date} timestamp - The timestamp to format
 * @returns {string} - Formatted date string
 */
export const formatDateSeparator = (timestamp) => {
  const date = convertToLocalTime(timestamp);
  if (!date) return timestamp || '-';
  
  try {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    if (messageDate.getTime() === today.getTime()) {
      return 'Today';
    } else if (messageDate.getTime() === yesterday.getTime()) {
      return 'Yesterday';
    } else {
      // Always show specific date format for better clarity (DD/MM/YYYY like WhatsApp)
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    }
  } catch (error) {
    console.error('Error formatting date separator:', error);
    return timestamp || '-';
  }
};

/**
 * Converts timestamps in a message object to local time
 * Recursively processes nested objects and arrays
 * CRITICAL: Preserves original timestamp values - does NOT overwrite with shared values
 * @param {Object} message - The message object to process
 * @returns {Object} - The message object with converted timestamps
 */
export const convertMessageTimestamps = (message) => {
  if (!message || typeof message !== 'object') return message;
  
  const converted = { ...message };
  
  // List of timestamp fields to convert
  const timestampFields = [
    'timestamp', 'time', 'date', 'createdAt', 'updatedAt',
    'lastMessageTime', 'lastMessageTimestamp'
  ];
  
  // CRITICAL: Store original timestamp values BEFORE conversion
  // This ensures each message maintains its unique timestamp
  const originalTimestamps = {};
  timestampFields.forEach(field => {
    if (converted[field]) {
      originalTimestamps[field] = converted[field];
    }
  });
  
  // Convert timestamp fields - preserve original values
  timestampFields.forEach(field => {
    if (converted[field]) {
      const localTime = convertToLocalTime(converted[field]);
      if (localTime) {
        // CRITICAL: Use the converted time for THIS specific message
        // Do NOT use a shared fallback that might be the same for all messages
        converted[field] = localTime.toISOString(); // Convert to ISO string for Redux serialization
      } else {
        // If conversion fails, preserve the original value
        converted[field] = originalTimestamps[field];
      }
    }
  });
  
  // Ensure both 'timestamp' and 'time' properties exist for compatibility
  // CRITICAL: Use values from THIS message, not from a shared source
  if (converted.timestamp && !converted.time) {
    converted.time = converted.timestamp;
  }
  if (converted.time && !converted.timestamp) {
    converted.timestamp = converted.time;
  }
  
  // Process nested objects - recursively preserve timestamps
  if (converted.chat && typeof converted.chat === 'object') {
    converted.chat = convertMessageTimestamps(converted.chat);
  }
  
  if (converted.lastMessage && typeof converted.lastMessage === 'object') {
    converted.lastMessage = convertMessageTimestamps(converted.lastMessage);
  }
  
  // Process arrays - each chat gets its own timestamp conversion
  if (Array.isArray(converted.chats)) {
    converted.chats = converted.chats.map(chat => convertMessageTimestamps(chat));
  }
  
  return converted;
};

/**
 * Converts timestamps in an array of messages to local time
 * @param {Array} messages - Array of message objects
 * @returns {Array} - Array of message objects with converted timestamps
 */
export const convertMessagesTimestamps = (messages) => {
  if (!Array.isArray(messages)) return messages;
  
  return messages.map(message => convertMessageTimestamps(message));
};
